// Messagerie store (web).
//
// Real-time: subscribes directly to Firestore via onSnapshot — the `discussions`
// collection (scoped to org + membership) and, for the open discussion, its
// `messages` subcollection. The backend writes those docs through the admin SDK;
// this store only reads them live and performs mutations over HTTP.

import { create } from "zustand";
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  setDoc,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../config/FirebaseConfig";
import messagerieApi from "../api/messagerie";

const MESSAGE_WINDOW = 100; // most-recent messages kept live per discussion

const useMessagerieStore = create((set, get) => ({
  // config
  prefix: null,
  orgId: null,
  userId: null,

  // data
  discussions: [],
  messages: {}, // { [discussionId]: Message[] (chronological) }
  unreadCounts: {}, // { [discussionId]: number }
  members: [], // org users [{id, display_name, ...}]
  readReceipts: {}, // { [discussionId]: ReadReceipt[] }

  // ui
  activeTab: "generales", // generales | ordres | privees
  selectedDiscussionId: null,
  isLoadingMessages: false,
  isLoadingOlderMessages: false,
  hasMoreMessages: {}, // { [discussionId]: bool } — false once the top is reached
  replyTarget: null, // message being replied to (composer)

  // internal listener handles
  _unsubDiscussions: null,
  _unsubMessages: null,
  _globalReady: false, // app-wide discussions listener installed
  _discPrimed: false, // first snapshot consumed (baseline)
  newMessagePing: 0, // bumps on each incoming message (drives NavBar animation)

  // ---- lifecycle ----
  // App-wide listener — call from a component that is always mounted (NavBar),
  // so the unread counter + new-message animation work even off the messagerie
  // page. Idempotent: only the first valid call installs the listener.
  initGlobal: ({ prefix, orgId, userId }) => {
    if (!prefix || !orgId || !userId) return;
    if (get()._globalReady) return;
    set({ prefix, orgId, userId, _globalReady: true, _discPrimed: false });

    get().refreshUnreadCounts();

    const q = query(
      collection(db, "discussions"),
      where("members", "array-contains", userId)
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const live = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((d) => d.org_id === orgId);
        if (!live.length) return;

        const state = get();
        const selId = state.selectedDiscussionId;
        const prevById = new Map(state.discussions.map((d) => [d.id, d]));

        // First snapshot = baseline; don't count existing messages as "new".
        if (!state._discPrimed) {
          set({ _discPrimed: true });
          get()._mergeDiscussions(live);
          return;
        }

        // `realtime_rev` is bumped by the backend on reactions / read receipts;
        // include it so those changes (which don't touch last_message) are
        // detected. `msgSig` tracks genuinely-new messages for the unread badge.
        const sig = (d) =>
          `${d?.last_message?.timestamp || ""}|${d?.updated_at || ""}|${d?.realtime_rev || ""}`;
        const msgSig = (d) => d?.last_message?.timestamp || "";
        const nextUnread = { ...state.unreadCounts };
        let pinged = false;

        for (const d of live) {
          const prev = prevById.get(d.id);
          if (prev && sig(d) === sig(prev)) continue; // nothing changed
          if (d.id === selId) {
            // Viewing this discussion → refetch messages AND read receipts so
            // new messages, reactions and blue ticks all appear live.
            get().fetchMessages(selId);
            get().fetchReadReceipts(selId);
          } else {
            // Only a genuinely new message (not a reaction/read bump) counts
            // toward the unread badge.
            const newMsg = !!prev && msgSig(d) !== msgSig(prev);
            const lastSender = d.last_message?.sender_id;
            if (newMsg && lastSender && lastSender !== userId) {
              nextUnread[d.id] = (nextUnread[d.id] || 0) + 1;
              pinged = true;
            }
          }
        }

        set({ unreadCounts: nextUnread });
        get()._mergeDiscussions(live);
        if (pinged) set((s) => ({ newMessagePing: s.newMessagePing + 1 }));
      },
      (err) => console.error("[messagerie] discussions listener error:", err)
    );
    set({ _unsubDiscussions: unsub });
  },

  // Page-level init (messagerie screen): ensure the global listener is up and
  // load the authoritative lists for the view.
  init: ({ prefix, orgId, userId, members = [] }) => {
    if (!prefix || !orgId || !userId) return;
    set({ members });
    get().initGlobal({ prefix, orgId, userId });
    get().refreshDiscussions();
    get().refreshUnreadCounts();
  },

  _sortDiscussions: (list) =>
    [...list].sort((a, b) =>
      (b.updated_at || "").localeCompare(a.updated_at || "")
    ),

  _mergeDiscussions: (live) => {
    set((state) => {
      const map = new Map(state.discussions.map((d) => [d.id, d]));
      live.forEach((d) => map.set(d.id, { ...(map.get(d.id) || {}), ...d }));
      return { discussions: get()._sortDiscussions([...map.values()]) };
    });
  },

  refreshDiscussions: async () => {
    const { prefix } = get();
    if (!prefix) return;
    try {
      const list = await messagerieApi.listDiscussions(prefix, { limit: 200 });
      if (Array.isArray(list)) set({ discussions: get()._sortDiscussions(list) });
    } catch (e) {
      console.warn("[messagerie] list discussions failed:", e.message);
    }
  },

  // Leaving the messagerie page: keep the app-wide discussions listener alive
  // (so the unread badge + animation keep working); just drop the selection.
  teardown: () => {
    set({ selectedDiscussionId: null });
  },

  setActiveTab: (tab) => set({ activeTab: tab }),

  setMembers: (members) => set({ members }),

  setReplyTarget: (msg) => set({ replyTarget: msg }),

  // Typing indicator — written/read directly in Firestore (heric mechanism):
  // discussions/{id}/typing/{userId}. The thread listens to that subcollection.
  sendTyping: (discussionId) => {
    const { userId } = get();
    if (!discussionId || !userId) return;
    const resolved = get().getMemberName(userId);
    setDoc(doc(db, "typing_status", `${discussionId}__${userId}`), {
      discussion_id: discussionId,
      user_id: userId,
      // Embed the resolved name so receivers don't depend on their own members
      // list to display "X écrit…" (empty when unresolved, never the raw id).
      user_name: resolved && resolved !== userId ? resolved : "",
      timestamp: serverTimestamp(),
    }).catch(() => {});
  },
  clearTyping: (discussionId) => {
    const { userId } = get();
    if (!discussionId || !userId) return;
    deleteDoc(doc(db, "typing_status", `${discussionId}__${userId}`)).catch(() => {});
  },

  // ---- discussions ----
  refreshUnreadCounts: async () => {
    const { prefix } = get();
    if (!prefix) return;
    try {
      const counts = await messagerieApi.getUnreadCounts(prefix);
      set({ unreadCounts: counts || {} });
    } catch (e) {
      console.warn("[messagerie] unread counts failed:", e.message);
    }
  },

  createDiscussion: async ({ tab, name, description, memberIds }) => {
    const { prefix } = get();
    const created = await messagerieApi.createDiscussion(prefix, {
      tab,
      name,
      description,
      member_ids: memberIds,
    });
    await get().refreshDiscussions();
    return created;
  },

  deleteDiscussion: async (id) => {
    const { prefix } = get();
    await messagerieApi.deleteDiscussion(prefix, id);
    if (get().selectedDiscussionId === id) get().selectDiscussion(null);
  },

  addMembers: async (id, userIds) => {
    const { prefix } = get();
    await messagerieApi.addMembers(prefix, id, userIds);
    await get().refreshDiscussions();
  },

  leaveDiscussion: async (id) => {
    const { prefix, userId } = get();
    await messagerieApi.removeMember(prefix, id, userId);
    if (get().selectedDiscussionId === id) get().selectDiscussion(null);
    await get().refreshDiscussions();
  },

  // ---- messages ----
  _mergeMessages: (discussionId, list) => {
    set((state) => {
      const existing = state.messages[discussionId] || [];
      const map = new Map(existing.map((m) => [m.id, m]));
      list.forEach((m) => map.set(m.id, { ...(map.get(m.id) || {}), ...m }));
      const merged = [...map.values()].sort((a, b) =>
        (a.created_at || "").localeCompare(b.created_at || "")
      );
      return { messages: { ...state.messages, [discussionId]: merged } };
    });
  },

  selectDiscussion: async (discussionId) => {
    set({ selectedDiscussionId: discussionId, replyTarget: null });
    if (!discussionId) return;
    set({ isLoadingMessages: true });
    await get().fetchMessages(discussionId);
    set({ isLoadingMessages: false });
    get().fetchReadReceipts(discussionId);
  },

  // Authoritative HTTP fetch of the latest message window, replacing the list.
  // The thread's Firestore listener calls this on every change (heric pattern),
  // which is what makes incoming messages appear live in the open conversation.
  fetchMessages: async (discussionId) => {
    const { prefix } = get();
    if (!prefix || !discussionId) return;
    try {
      const list = await messagerieApi.listMessages(prefix, discussionId, {
        limit: MESSAGE_WINDOW,
      });
      if (!Array.isArray(list)) return;
      set((state) => ({
        messages: { ...state.messages, [discussionId]: list },
        // A full window back implies there may be older messages to page in.
        hasMoreMessages: {
          ...state.hasMoreMessages,
          [discussionId]: list.length >= MESSAGE_WINDOW,
        },
      }));
      const last = list[list.length - 1];
      if (last) {
        messagerieApi.markAsRead(prefix, discussionId, last.id).catch(() => {});
        set((state) => ({
          unreadCounts: { ...state.unreadCounts, [discussionId]: 0 },
        }));
      }
    } catch (e) {
      console.warn("[messagerie] fetch messages failed:", e.message);
    }
  },

  loadOlderMessages: async (discussionId) => {
    const { prefix, messages, isLoadingOlderMessages, hasMoreMessages } = get();
    if (isLoadingOlderMessages) return;
    if (hasMoreMessages[discussionId] === false) return;
    const current = messages[discussionId] || [];
    if (!current.length) return;

    const before = current[0].id;
    set({ isLoadingOlderMessages: true });
    try {
      const older = await messagerieApi.listMessages(prefix, discussionId, {
        before,
        limit: 50,
      });
      if (Array.isArray(older)) {
        // Reached the top once a partial/empty page comes back.
        if (older.length < 50) {
          set((s) => ({
            hasMoreMessages: { ...s.hasMoreMessages, [discussionId]: false },
          }));
        }
        if (older.length) {
          set((s) => {
            const cur = s.messages[discussionId] || [];
            const ids = new Set(cur.map((m) => m.id));
            const merged = [...older.filter((m) => !ids.has(m.id)), ...cur];
            return { messages: { ...s.messages, [discussionId]: merged } };
          });
        }
      }
    } catch (e) {
      console.warn("[messagerie] load older failed:", e.message);
    } finally {
      set({ isLoadingOlderMessages: false });
    }
  },

  // Insert/replace a message by id (keeps chronological order). Lets sends/edits
  // show instantly even before the Firestore listener echoes the change back.
  _upsertMessage: (discussionId, msg) => {
    if (!msg || !msg.id) return;
    get()._mergeMessages(discussionId, [msg]);
  },

  sendMessage: async (discussionId, { text, attachment, attachments, replyToId, mentions, folderRefs }) => {
    const { prefix } = get();
    const msg = await messagerieApi.sendMessage(prefix, discussionId, {
      text,
      attachment,
      attachments,
      reply_to_id: replyToId,
      mentions,
      folder_refs: folderRefs,
    });
    get()._upsertMessage(discussionId, msg);
    get().refreshDiscussions(); // update sidebar last_message
    return msg;
  },

  uploadAttachment: async (discussionId, file, onProgress) => {
    const { prefix } = get();
    // Large files (videos) can't go through the backend in prod (Cloud Run's
    // 32 MiB HTTP/1 cap), so upload them straight to GCS via a signed URL.
    // Small files stay proxied so the backend can also build PDF previews.
    const DIRECT_MIN = 30 * 1024 * 1024;
    if (file.size > DIRECT_MIN) {
      const contentType = file.type || "application/octet-stream";
      const meta = await messagerieApi.getSignedUpload(prefix, discussionId, {
        filename: file.name,
        content_type: contentType,
        size: file.size,
      });
      await messagerieApi.putToSignedUrl(meta.upload_url, file, contentType, onProgress);
      return {
        gcs_path: meta.gcs_path,
        name: meta.name,
        mime_type: meta.mime_type,
        size: meta.size,
        preview_gcs_path: null,
        pages: null,
      };
    }
    return messagerieApi.uploadAttachment(prefix, discussionId, file, onProgress);
  },

  editMessage: async (discussionId, messageId, text) => {
    const { prefix } = get();
    const msg = await messagerieApi.updateMessage(prefix, discussionId, messageId, text);
    get()._upsertMessage(discussionId, msg);
    get().refreshDiscussions(); // editing the last message changes the sidebar preview
    return msg;
  },

  deleteMessage: async (discussionId, messageId) => {
    const { prefix } = get();
    const res = await messagerieApi.deleteMessage(prefix, discussionId, messageId);
    // Optimistically mark deleted (listener will confirm).
    set((state) => {
      const list = state.messages[discussionId] || [];
      return {
        messages: {
          ...state.messages,
          [discussionId]: list.map((m) =>
            m.id === messageId ? { ...m, deleted: true, text: null, attachment: null, reactions: [] } : m
          ),
        },
      };
    });
    return res;
  },

  toggleReaction: async (discussionId, messageId, emoji) => {
    const { prefix } = get();
    const msg = await messagerieApi.toggleReaction(prefix, discussionId, messageId, emoji);
    get()._upsertMessage(discussionId, msg);
    return msg;
  },

  // Pin / unpin a message. The backend returns the updated discussion (with its
  // pinned_messages array); we merge it straight in so the pinned banner updates
  // instantly, and the Firestore realtime bump reconciles other members' views.
  pinMessage: async (discussionId, messageId) => {
    const { prefix } = get();
    const updated = await messagerieApi.pinMessage(prefix, discussionId, messageId);
    if (updated) get()._mergeDiscussions([updated]);
    return updated;
  },
  unpinMessage: async (discussionId, messageId) => {
    const { prefix } = get();
    const updated = await messagerieApi.unpinMessage(prefix, discussionId, messageId);
    if (updated) get()._mergeDiscussions([updated]);
    return updated;
  },

  fetchReadReceipts: async (discussionId) => {
    const { prefix } = get();
    try {
      const receipts = await messagerieApi.getReadReceipts(prefix, discussionId);
      set((state) => ({
        readReceipts: { ...state.readReceipts, [discussionId]: receipts || [] },
      }));
    } catch (e) {
      // non-fatal
    }
  },

  // ---- selectors ----
  getFilteredDiscussions: () => {
    const { discussions, activeTab } = get();
    return discussions.filter((d) => (d.tab || "generales") === activeTab);
  },
  getTotalUnread: () => {
    const { unreadCounts } = get();
    return Object.values(unreadCounts).reduce((a, b) => a + (b || 0), 0);
  },
  getMemberName: (id) => {
    const m = get().members.find((u) => u.id === id);
    return (m && (m.display_name || m.full_name || m.email)) || id;
  },
}));

export default useMessagerieStore;
